<template>
  <q-card-section class="cmp-quick-order q-pa-none">
    <ws-button
      :type="loggedIn ? WsButtonEnum.Type.TERTIARY_NEUTRAL : WsButtonEnum.Type.PRIMARY"
      :size="WsButtonEnum.Size.XL"
      :disable="loading"
      :loading="loading"
      loader-color="dark"
      full-width
      :data-test="testNames.quickOrderButton"
      @click="goNext"
    >
      {{ $t('summary.continue_btn') }}
    </ws-button>
  </q-card-section>
</template>

<script lang="ts">
// @ts-nocheck -- baseline for pre-existing errors so typecheck can be turned on
import { Component, Vue } from 'vue-property-decorator';
import { mapGetters } from 'vuex';
import { type AxiosError } from 'axios';
import { QCardSection } from 'quasar';

import { redirect } from '@odin/utils';
import { WsButton, WsButtonEnum, WsDelimiter } from '@odin/components-vue2';
import { checkQueryParam, getLoginUrl } from '@/utilities';
import { STEPS } from '@/services/const.enum';
import {
  canFillMissingDomainFields,
  goToFillMissingDomainFields,
  navigateToStep,
  performQuickOrder,
  setUserStep,
} from '@/services/order.service';
import { ROUTENAMES } from '@/consts/const.enum';
import { SidePanels, StateMutations } from '@/store/const.enum';
import { useMessages } from '@odin/services';

const {handleUnstructuredError, deleteMessage} = useMessages();

export const testNames = process.env.NODE_ENV === 'test' ? {
  quickOrderButton: 'quick-order-button',
} : {} as Record<string, string>;

@Component({
  components: {
    QCardSection,
    WsButton,
    WsDelimiter,
  },
  computed: {
    ...mapGetters(['firstStepWithIssues', 'loggedIn']),
  },
})
export default class QuickOrderButton extends Vue {
  testNames = testNames;
  readonly firstStepWithIssues!: STEPS | null;
  readonly loggedIn!: boolean;

  loading: boolean = false;
  path: string = 'cart.summary.quick_order';
  WsButtonEnum = WsButtonEnum;

  mounted() {
    const searchParam = checkQueryParam('orderRefId');
    if (searchParam.exists) {
      this.openSideBar();
    }
  }

  login() {
    redirect(getLoginUrl(this.$router, ROUTENAMES.CART));
  }

  goNext() {
    if (!this.loggedIn) {
      this.openSideBar();

      return;
    }

    this.startQuickOrder();
  }

  openSideBar() {
    this.$store.commit(StateMutations.MERGE_STATE, {
      ui: { sidePanel: SidePanels.Login },
    });
  }

  async startQuickOrder(): Promise<any> {
    deleteMessage(this.path);
    this.loading = true;

    try {
      await navigateToStep(STEPS.BILLING_PROFILE, false);
    } catch (error) {
      this.loading = false;
      // do not handle navigation error as it is handled in navigation guard
      return Promise.reject();
    }

    return performQuickOrder()
      .then(() => setUserStep(STEPS.RECAPITULATION))
      .catch(this.handleError)
      .finally(() => (this.loading = false));
  }

  async goToRecapitulation() {
    await setUserStep(STEPS.RECAPITULATION);
    await navigateToStep(STEPS.BILLING_PROFILE, false);
  }

  // Example error can be found in checkout.mock.ts, see 'const quickOrderMultiError'...
  async handleError(error: AxiosError) {
    const { status }: { status: number } = error?.response?.data;

    // Unauthorized
    if (status === 401) {
      this.login();
      return;
    }

    if (canFillMissingDomainFields()) {
      await goToFillMissingDomainFields();
    } else {
      await this.goToRecapitulation();
    }
    handleUnstructuredError(this.path, error);
  }
}
</script>

<style lang="scss" scoped>
.cmp-quick-order {
  .login-text {
    color: $ws-base100;
  }
}
</style>
